import {getLocalizationBasedOnLang} from '../../../../data/models/User/UserInfo';

/*
 * The storyboard page: drafting, the page's notices, each microreel's storyboard, adding a
 * scene, the microreel list and the call to action under it. `clipping` jobs cut their
 * microreels from a video, so their scenes are clips.
 */

const microreels = (count: number) =>
  count === 1 ? 'microreel' : 'microreels';

const english = {
  loading: 'Loading…',
  reloadStoryboard: 'Reload storyboard',

  // Drafting: what is done, under way and still to come.
  draftingTitle: 'Drafting your storyboard',
  mayTakeMinutes: 'This may take a few minutes.',
  mayTakeMinute: 'This may take a minute.',
  keepWorking: 'You can leave and come back; we’ll keep working.',
  elapsed: (seconds: number) => `${seconds}s elapsed`,
  draftNote:
    'Review and edit your storyboard before generating your microreels. AI credits are used when creating or regenerating content.',
  stepRead: 'Read the document (text per page, OCR for scanned pages)',
  stepPlan: 'Plan the storyboard and write the script',
  stepCheck: 'Check every scene against the source',
  // A video has no pages: Studio transcribes it and works from the transcript.
  stepTranscribeAndRead: 'Transcribe the video and read the transcript',
  stepCheckTranscript: 'Check every scene against the transcript',
  // Clipping: the planner picks the clips, then Studio takes each one's first frame.
  stepTranscribe: 'Transcribe the video',
  stepChooseClips: (perTopic: boolean) =>
    `Choose the clips for your ${perTopic ? 'microreels' : 'microreel'}`,
  stepFirstFrames: 'Take the first frame of each clip',

  // The page's notices.
  savedUpToDate: (count: number) =>
    `Storyboard saved. Your ${
      count === 1 ? 'microreel is' : 'microreels are'
    } still up to date; share again to send the change to learners.`,
  savedRegenerate: (count: number, clipping: boolean) =>
    `Storyboard saved. Regenerate to update your ${microreels(
      count,
    )}; unchanged ${clipping ? 'clips' : 'scenes'} are reused.`,
  waitForDrafting: 'Wait for the storyboard to finish drafting.',
  waitForGenerating: (count: number) =>
    `Wait for your ${microreels(count)} to finish generating.`,
  waitForStudio: 'Wait for the current Studio operation to finish.',
  renameUnsupported:
    "This Studio server can't rename microreels yet. It needs the latest Studio API.",
  sourceSaved: (video: boolean) =>
    `Your ${video ? 'video' : 'document'} and settings are saved.`,
  settingsSaved: 'Your settings are saved.',
  tryAgain: 'Try again',
  tryingAgain: 'Trying again…',
  perTopicIgnored:
    "You asked for one microreel per topic, but this Studio server doesn't support that yet, so it planned a single microreel. Once Studio is updated, create it again to get one per topic.",

  // The page head.
  headerMeta: (count: number, scenes: string, duration: string) =>
    `${count} ${microreels(count)} · ${scenes} · ~${duration} · Storyboard`,
  sceneCount: (count: number, clipping: boolean) =>
    clipping ? `${count} ${count === 1 ? 'clip' : 'clips'}` : `${count} scenes`,

  // Generating.
  generate: 'Generate',
  regenerate: 'Regenerate',
  generating: 'Generating…',
  saveChanges: 'Save changes',
  watch: (count: number) => `Watch ${microreels(count)}`,
  watchProgress: 'Watch progress',
  saveEditsFirst: (clipping: boolean) =>
    `Save your ${clipping ? 'clip' : 'scene'} edits before generating.`,
  endingSavedOnGenerate: (attestation: boolean) =>
    `Your unsaved ${
      attestation ? 'policy attestation' : 'knowledge check'
    } changes are saved when you generate.`,

  // The call to action under the microreel list.
  generateRegion: 'Generate microreels',
  ctaGenerating: (count: number) => `Generating your ${microreels(count)}…`,
  // Generating after a render that stopped part way: the ones it made play already.
  ctaGeneratingRemaining: (remaining: number) =>
    remaining === 1
      ? 'Generating the remaining microreel…'
      : `Generating the remaining ${remaining} microreels…`,
  ctaUnsaved: (clipping: boolean) =>
    `You have unsaved ${clipping ? 'clip' : 'scene'} edits`,
  ctaSaveChanges: 'Save your changes',
  ctaUpToDate: (count: number) =>
    `Your ${count === 1 ? 'microreel is' : 'microreels are'} up to date`,
  ctaRegenerate: 'Regenerate with your changes',
  ctaReady: 'Ready to make your microreels?',
  hintGenerating:
    'Watch the progress and preview each microreel as it is ready.',
  hintUnsaved: (count: number) =>
    `Save or revert them before generating your ${microreels(count)}.`,
  hintSavesOnly:
    'What learners answer after the microreel is not part of it, so nothing is generated again.',
  hintUpToDate: (count: number): string =>
    count === 1
      ? 'Watch it, then share it with learners.'
      : 'Watch them, then share them with learners.',
  hintEdited: (clipping: boolean): string =>
    clipping
      ? 'Only changed clips are cut again.'
      : 'Only changed scenes are made again.',
  // After a render that stopped part way and edits that leave none of the ones it made to keep
  // (see unmadeReels).
  hintUnmadeFirst: (unmade: number, others: number): string =>
    `Generating makes ${
      unmade === 1 ? 'the microreel' : `the ${unmade} microreels`
    } not generated yet first, then ${
      others === 1 ? 'the other one' : `the other ${others}`
    }.`,
  hintRemake: (count: number): string =>
    count === 1
      ? 'Makes the microreel again from this storyboard.'
      : `Makes all ${count} microreels again from these storyboards.`,
  hintCreate: (count: number): string =>
    count === 1
      ? 'Creates the microreel from this storyboard.'
      : `Creates all ${count} microreels from these storyboards.`,
  openReel: (title: string) => `Open ${title}`,
  beforeGenerating: 'Before generating',
  andMore: (count: number) => `…and ${count} more`,
  endingsSavedOnGenerate: (attestation: boolean) =>
    `Unsaved ${
      attestation ? 'policy attestation' : 'knowledge check'
    } changes are saved when you generate.`,

  // The microreel list.
  listLabel: 'Microreels in this storyboard',
  listHeading: (count: number) => `${count} ${microreels(count)}`,
  listSubtitle: (duration: string) => `Storyboard · ~${duration} in total`,
  selectToEdit: 'Select one to edit it',
  chipUnsaved: 'Unsaved edits',
  chipNeedsConfirmation: 'Needs confirmation',
  chipToFix: (count: number) => `${count} to fix`,
  chipCheck: 'Check',
  chipChanged: 'Changed',
  // After a render that stopped part way: made by it, or still to generate.
  chipGenerated: 'Generated',
  chipNotGenerated: 'Not generated yet',
  reelMeta: (scenes: string, duration: string) => `${scenes} · ~${duration}`,

  // Theme and branding, above the selected microreel.
  themeSummary: 'Theme · all microreels',
  objective: 'Objective',
  message: 'Message',
  hook: 'Hook',
  brandingSummary: 'Video settings & branding · all microreels',

  // One microreel's storyboard.
  reelPosition: (index: number, count: number) =>
    `Microreel ${index} of ${count}`,
  policyAttestation: 'Policy attestation',
  knowledgeCheck: 'Knowledge check',
  editEnding: (attestation: boolean): string =>
    attestation ? 'Edit the policy attestation' : 'Edit the knowledge check',
  endingFor: (attestation: boolean, title: string) =>
    `${attestation ? 'Policy attestation' : 'Knowledge check'} for ${title}`,
  aboutReel: 'About this microreel',
  scenes: 'Scenes',
  clips: 'Clips',
  atTheEnd: 'At the end of the microreel',
  otherReels: 'Other microreels',

  // Adding a scene the AI did not write.
  addScene: 'Add scene',
  addSceneFirst: 'Add scene at the start',
  addSceneAfter: (scene: number) => `Add scene after scene ${scene}`,
  sceneLimit: (max: number) => `A microreel holds at most ${max} scenes.`,
  newScene: 'New scene',
  newSceneNarration: 'Narration',
  newSceneNarrationPlaceholder: 'What the narrator says in this scene',
  newSceneOnScreen: 'On-screen text',
  newSceneOnScreenPlaceholder: 'A few words shown in the scene',
  newScenePage: 'Based on',
  newScenePagePlaceholder: 'Choose the page this scene comes from',
  sourcePage: (page: number) => `Page ${page}`,
  newSceneHint:
    'The new scene joins this microreel; edit it like any other scene before generating.',
  adding: 'Adding…',
  cancel: 'Cancel',
  narrationRequired: 'Write what the new scene says.',
  onScreenRequired: 'Write the words shown on screen in the new scene.',
  pageRequired: 'Choose the page of the document this scene is based on.',
};

const welsh: typeof english = {
  loading: 'Yn llwytho…',
  reloadStoryboard: 'Ail-lwytho’r bwrdd stori',

  draftingTitle: 'Yn drafftio eich bwrdd stori',
  mayTakeMinutes: 'Gall hyn gymryd ychydig funudau.',
  mayTakeMinute: 'Gall hyn gymryd munud.',
  keepWorking: 'Gallwch adael a dod yn ôl; byddwn ni’n dal ati.',
  elapsed: (seconds: number) => `${seconds} eiliad wedi mynd heibio`,
  draftNote:
    'Adolygwch a golygwch eich bwrdd stori cyn cynhyrchu eich microreels. Defnyddir credydau AI wrth greu neu ail-greu cynnwys.',
  stepRead:
    'Darllen y ddogfen (testun pob tudalen, OCR ar gyfer tudalennau wedi’u sganio)',
  stepPlan: 'Cynllunio’r bwrdd stori ac ysgrifennu’r sgript',
  stepCheck: 'Gwirio pob golygfa yn erbyn y ffynhonnell',
  stepTranscribeAndRead: 'Trawsgrifio’r fideo a darllen y trawsgrifiad',
  stepCheckTranscript: 'Gwirio pob golygfa yn erbyn y trawsgrifiad',
  stepTranscribe: 'Trawsgrifio’r fideo',
  stepChooseClips: (perTopic: boolean) =>
    `Dewis y clipiau ar gyfer eich ${perTopic ? 'microreels' : 'microreel'}`,
  stepFirstFrames: 'Cymryd ffrâm gyntaf pob clip',

  savedUpToDate: (count: number) =>
    `Bwrdd stori wedi’i gadw. Mae eich ${microreels(
      count,
    )} yn dal yn gyfredol; rhannwch eto i anfon y newid at ddysgwyr.`,
  savedRegenerate: (count: number, clipping: boolean) =>
    `Bwrdd stori wedi’i gadw. Ail-gynhyrchwch i ddiweddaru eich ${microreels(
      count,
    )}; mae ${
      clipping ? 'clipiau' : 'golygfeydd'
    } heb eu newid yn cael eu hailddefnyddio.`,
  waitForDrafting: 'Arhoswch i’r bwrdd stori orffen drafftio.',
  waitForGenerating: (count: number) =>
    `Arhoswch i’ch ${microreels(count)} orffen cynhyrchu.`,
  waitForStudio: 'Arhoswch i’r gwaith presennol yn Studio orffen.',
  renameUnsupported:
    'Ni all y gweinydd Studio hwn ailenwi microreels eto. Mae angen y fersiwn ddiweddaraf o API Studio arno.',
  sourceSaved: (video: boolean) =>
    `Mae eich ${video ? 'fideo' : 'dogfen'} a’ch gosodiadau wedi’u cadw.`,
  settingsSaved: 'Mae eich gosodiadau wedi’u cadw.',
  tryAgain: 'Rhoi cynnig arall arni',
  tryingAgain: 'Yn rhoi cynnig arall arni…',
  perTopicIgnored:
    'Gofynnoch am un microreel ar gyfer pob pwnc, ond nid yw’r gweinydd Studio hwn yn cefnogi hynny eto, felly cynlluniodd un microreel. Pan fydd Studio wedi’i ddiweddaru, crëwch ef eto i gael un ar gyfer pob pwnc.',

  // Welsh counts take the singular noun.
  headerMeta: (count: number, scenes: string, duration: string) =>
    `${count} microreel · ${scenes} · ~${duration} · Bwrdd stori`,
  sceneCount: (count: number, clipping: boolean) =>
    `${count} ${clipping ? 'clip' : 'golygfa'}`,

  generate: 'Cynhyrchu',
  regenerate: 'Ail-gynhyrchu',
  generating: 'Yn cynhyrchu…',
  saveChanges: 'Cadw’r newidiadau',
  watch: (count: number) => `Gwylio’r ${microreels(count)}`,
  watchProgress: 'Gwylio’r cynnydd',
  saveEditsFirst: (clipping: boolean) =>
    `Cadwch eich golygiadau i’r ${
      clipping ? 'clipiau' : 'golygfeydd'
    } cyn cynhyrchu.`,
  endingSavedOnGenerate: (attestation: boolean) =>
    `Bydd eich newidiadau heb eu cadw i’r ${
      attestation ? 'datganiad polisi' : 'gwiriad gwybodaeth'
    } yn cael eu cadw pan fyddwch yn cynhyrchu.`,

  generateRegion: 'Cynhyrchu microreels',
  ctaGenerating: (count: number) => `Yn cynhyrchu eich ${microreels(count)}…`,
  ctaGeneratingRemaining: (remaining: number) =>
    remaining === 1
      ? 'Yn cynhyrchu’r microreel sy’n weddill…'
      : `Yn cynhyrchu’r ${remaining} microreel sy’n weddill…`,
  ctaUnsaved: (clipping: boolean) =>
    `Mae gennych olygiadau heb eu cadw i’r ${
      clipping ? 'clipiau' : 'golygfeydd'
    }`,
  ctaSaveChanges: 'Cadwch eich newidiadau',
  ctaUpToDate: (count: number) => `Mae eich ${microreels(count)} yn gyfredol`,
  ctaRegenerate: 'Ail-gynhyrchu gyda’ch newidiadau',
  ctaReady: 'Yn barod i wneud eich microreels?',
  hintGenerating:
    'Gwyliwch y cynnydd a chael rhagolwg o bob microreel pan fydd yn barod.',
  hintUnsaved: (count: number) =>
    `Cadwch neu ddadwneud y golygiadau cyn cynhyrchu eich ${microreels(
      count,
    )}.`,
  hintSavesOnly:
    'Nid yw’r hyn y mae dysgwyr yn ei ateb ar ôl y microreel yn rhan ohono, felly nid oes dim yn cael ei gynhyrchu eto.',
  hintUpToDate: (count: number) =>
    count === 1
      ? 'Gwyliwch ef, yna rhannwch ef gyda dysgwyr.'
      : 'Gwyliwch nhw, yna rhannwch nhw gyda dysgwyr.',
  hintEdited: (clipping: boolean) =>
    clipping
      ? 'Dim ond clipiau sydd wedi newid sy’n cael eu torri eto.'
      : 'Dim ond golygfeydd sydd wedi newid sy’n cael eu gwneud eto.',
  // "Heb ei gynhyrchu" (one microreel) and "heb eu cynhyrchu" (several).
  hintUnmadeFirst: (unmade: number, others: number) =>
    `Bydd cynhyrchu yn gwneud ${
      unmade === 1
        ? 'y microreel sydd heb ei gynhyrchu eto'
        : `y ${unmade} microreel sydd heb eu cynhyrchu eto`
    } yn gyntaf, yna’r ${others === 1 ? 'llall' : `${others} arall`}.`,
  hintRemake: (count: number) =>
    count === 1
      ? 'Yn gwneud y microreel eto o’r bwrdd stori hwn.'
      : `Yn gwneud pob un o’r ${count} microreel eto o’r byrddau stori hyn.`,
  hintCreate: (count: number) =>
    count === 1
      ? 'Yn creu’r microreel o’r bwrdd stori hwn.'
      : `Yn creu pob un o’r ${count} microreel o’r byrddau stori hyn.`,
  openReel: (title: string) => `Agor ${title}`,
  beforeGenerating: 'Cyn cynhyrchu',
  andMore: (count: number) => `…a ${count} arall`,
  endingsSavedOnGenerate: (attestation: boolean) =>
    `Bydd newidiadau heb eu cadw i’r ${
      attestation ? 'datganiad polisi' : 'gwiriad gwybodaeth'
    } yn cael eu cadw pan fyddwch yn cynhyrchu.`,

  listLabel: 'Microreels yn y bwrdd stori hwn',
  listHeading: (count: number) => `${count} microreel`,
  listSubtitle: (duration: string) => `Bwrdd stori · ~${duration} i gyd`,
  selectToEdit: 'Dewiswch un i’w olygu',
  chipUnsaved: 'Golygiadau heb eu cadw',
  chipNeedsConfirmation: 'Angen cadarnhad',
  chipToFix: (count: number) => `${count} i’w trwsio`,
  chipCheck: 'Gwirio',
  chipChanged: 'Wedi newid',
  chipGenerated: 'Wedi’i gynhyrchu',
  chipNotGenerated: 'Heb ei gynhyrchu eto',
  reelMeta: (scenes: string, duration: string) => `${scenes} · ~${duration}`,

  themeSummary: 'Thema · pob microreel',
  objective: 'Amcan',
  message: 'Neges',
  hook: 'Bachyn',
  brandingSummary: 'Gosodiadau a brandio’r fideo · pob microreel',

  reelPosition: (index: number, count: number) =>
    `Microreel ${index} o ${count}`,
  policyAttestation: 'Datganiad polisi',
  knowledgeCheck: 'Gwiriad gwybodaeth',
  editEnding: (attestation: boolean) =>
    attestation ? 'Golygu’r datganiad polisi' : 'Golygu’r gwiriad gwybodaeth',
  endingFor: (attestation: boolean, title: string) =>
    `${
      attestation ? 'Datganiad polisi' : 'Gwiriad gwybodaeth'
    } ar gyfer ${title}`,
  aboutReel: 'Am y microreel hwn',
  scenes: 'Golygfeydd',
  clips: 'Clipiau',
  atTheEnd: 'Ar ddiwedd y microreel',
  otherReels: 'Microreels eraill',

  addScene: 'Ychwanegu golygfa',
  addSceneFirst: 'Ychwanegu golygfa ar y dechrau',
  addSceneAfter: (scene: number) => `Ychwanegu golygfa ar ôl golygfa ${scene}`,
  sceneLimit: (max: number) =>
    `Gall microreel gynnwys ${max} golygfa ar y mwyaf.`,
  newScene: 'Golygfa newydd',
  newSceneNarration: 'Naratif',
  newSceneNarrationPlaceholder:
    'Beth mae’r adroddwr yn ei ddweud yn yr olygfa hon',
  newSceneOnScreen: 'Testun ar y sgrin',
  newSceneOnScreenPlaceholder: 'Ychydig eiriau a ddangosir yn yr olygfa',
  newScenePage: 'Yn seiliedig ar',
  newScenePagePlaceholder: 'Dewiswch y dudalen y daw’r olygfa hon ohoni',
  sourcePage: (page: number) => `Tudalen ${page}`,
  newSceneHint:
    'Mae’r olygfa newydd yn ymuno â’r microreel hwn; golygwch hi fel unrhyw olygfa arall cyn cynhyrchu.',
  adding: 'Yn ychwanegu…',
  cancel: 'Canslo',
  narrationRequired: 'Ysgrifennwch beth mae’r olygfa newydd yn ei ddweud.',
  onScreenRequired:
    'Ysgrifennwch y geiriau a ddangosir ar y sgrin yn yr olygfa newydd.',
  pageRequired:
    'Dewiswch dudalen y ddogfen y mae’r olygfa hon yn seiliedig arni.',
};

export const storyboardText: typeof english = getLocalizationBasedOnLang(
  english,
  welsh,
);
